import { useLayoutContext } from 'contexts/LayoutContext';
import api from 'lib/trpcClient';
import Spacing from 'components/Spacing';
import PageLoader from 'components/PageLoader';
import { Typography } from '@mui/material';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import AccountMappingGrid from './AccountMappingGrid';
import buildTransactionPairs from './buildTransactionPairs';
import { TransactionOperation } from 'further-types/lp/transaction';
import { ActionTransactionSubTypes } from 'further-types/lp/action';
import type { AccountMapping } from 'further-types/lp/account';

const AccountMapping: React.FC = () => {
  const { selectedVehicleId } = useLayoutContext();
  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const [vehicle] = api.vehicle.getById.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  const [actionTypes] = api.action.listTypes.useSuspenseQuery({
    vehicleId: selectedVehicleId ?? '',
  });

  if (transactionTypesLoading) {
    return <PageLoader />;
  }

  const defaultAccountMappings = vehicle?.defaultAccountMappings ?? [];

  const formattedDefaultValues = buildTransactionPairs(defaultAccountMappings);

  // Add any rows for actions that don't already have transaction type mappings
  actionTypes?.forEach((action) => {
    if (
      !formattedDefaultValues.find(
        (mapping) =>
          mapping.actionType === action.actionType &&
          mapping.customActionType === action.customActionType,
      )
    ) {
      if (!action.subTypes.length) {
        formattedDefaultValues.push({
          actionRef: action.action,
          actionType: action.actionType,
          customActionTypeId: action.customActionTypeId,
          actionCategory: action.actionCategory,
          customActionType: action.customActionType,
          transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
          debitTransactionTypeId: '',
          creditTransactionTypeId: '',
          isInUse: false,
        });
      } else {
        action.subTypes.forEach((subType) => {
          formattedDefaultValues.push({
            actionRef: action.action,
            actionType: action.actionType,
            customActionTypeId: action.customActionTypeId,
            actionCategory: action.actionCategory,
            customActionType: action.customActionType,
            transactionSubType: subType.type,
            debitTransactionTypeId:
              subType.operationType === TransactionOperation.Debit ? '' : null,
            creditTransactionTypeId:
              subType.operationType === TransactionOperation.Credit ? '' : null,
            isInUse: false,
          });
        });
      }
    }
  });

  if (transactionTypeOptions.length === 0) {
    return (
      <Typography>
        To set account mappings, first create some transaction types in{' '}
        <em>Chart of accounts</em>.
      </Typography>
    );
  }

  return (
    <Spacing>
      <AccountMappingGrid
        vehicleId={selectedVehicleId ?? ''}
        transactionTypes={transactionTypeOptions}
        actionTypes={actionTypes ?? []}
        defaultAccountMappings={formattedDefaultValues}
      />
    </Spacing>
  );
};

export default AccountMapping;
